import React, { useEffect, useMemo, useState } from 'react';
import QuadrantChart from './QuadrantChart.jsx';
import BrochureIntro from './BrochureIntro.jsx';
import SubmissionsView from './SubmissionsView.jsx';
import {
  computeAxisScores,
  computeTechnicalScore,
  resolveQuadrant,
  flattenQuestions,
  totalQuestions,
  techBand,
  rationaleKey,
} from './scoring.js';

/**
 * MechanicAssessment
 *
 * JSON-driven candidate self-screen. Pure renderer over the instrument JSON.
 *
 * Flow: brochure → email gate → questions → result
 *
 * Props:
 *  - instrument (object): parsed instrument JSON. Required unless instrumentUrl is given.
 *  - instrumentUrl (string): fetch instrument JSON from this URL on mount.
 *  - leadEndpoint (string): URL to POST lead capture (default /api/leads).
 *  - submissionEndpoint (string): URL template to POST results, with ":sessionId"
 *    placeholder. Default /api/submissions/:sessionId.
 *  - onLeadCapture (async function): override lead POST. Called with
 *    { instrumentId, instrumentVersion, email, name, phone, experienceYears }.
 *    Must return { ok, sessionId, attemptNumber, message? }.
 *  - onSubmit (async function): override result POST. Called with sessionId and
 *    { responses, axisScores, technicalScore, quadrantId, completedAt }.
 *    Should return { ok, message? }.
 *  - adminMode (boolean): show instrument preview at /?admin=1.
 *  - submissionsMode (boolean): show admin submissions table at /?submissions=1.
 *  - adminToken (string): token passed to the admin submissions endpoint.
 *  - adminEndpoint (string): URL the submissions view fetches from
 *    (default /api/admin/submissions). PIN appended as ?token=.
 *  - branding (object): { companyName, logoUrl }.
 */
export default function MechanicAssessment({
  instrument: instrumentProp,
  instrumentUrl,
  leadEndpoint = '/api/leads',
  submissionEndpoint = '/api/submissions/:sessionId',
  onLeadCapture,
  onSubmit,
  adminMode = false,
  submissionsMode = false,
  adminToken,
  adminEndpoint = '/api/admin/submissions',
  branding = {},
}) {
  const [instrument, setInstrument] = useState(instrumentProp || null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!instrumentProp && instrumentUrl) {
      fetch(instrumentUrl)
        .then((r) => r.json())
        .then(setInstrument)
        .catch((e) => setLoadError(String(e)));
    }
  }, [instrumentProp, instrumentUrl]);

  // NOTE: every branch that renders .via-* markup must sit inside .via-root —
  // the themable CSS custom properties (--via-accent, etc.) are scoped there.
  // The candidate flow wraps itself below; these early returns must wrap too,
  // or their colors silently resolve to `transparent`/`currentcolor`.
  if (loadError)
    return <div className="via-root"><div className="via-error">Failed to load assessment: {loadError}</div></div>;
  if (!instrument)
    return <div className="via-root"><div className="via-loading">Loading assessment…</div></div>;
  if (submissionsMode)
    return (
      <div className="via-root">
        <SubmissionsView instrument={instrument} adminToken={adminToken} adminEndpoint={adminEndpoint} />
      </div>
    );
  if (adminMode)
    return (
      <div className="via-root">
        <AdminView instrument={instrument} />
      </div>
    );

  const seedDemo =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('seed') === 'demo';

  return (
    <CandidateFlow
      instrument={instrument}
      leadEndpoint={leadEndpoint}
      submissionEndpoint={submissionEndpoint}
      onLeadCapture={onLeadCapture}
      onSubmit={onSubmit}
      branding={branding}
      seedDemo={seedDemo}
    />
  );
}

// ---------------------------------------------------------------------------
// Candidate flow: brochure → email-gate → questions → result
// ---------------------------------------------------------------------------

function CandidateFlow({
  instrument,
  leadEndpoint,
  submissionEndpoint,
  onLeadCapture,
  onSubmit,
  branding,
  seedDemo = false,
}) {
  const [stage, setStage] = useState(seedDemo ? 'questions' : 'brochure'); // brochure | gate | questions | result
  const [lead, setLead] = useState(
    seedDemo
      ? { ...DEV_PREFILL, sessionId: `demo-${Date.now()}`, attemptNumber: 1 }
      : null
  );          // { sessionId, attemptNumber, ...fields }
  const [responses, setResponses] = useState({});
  const [cursor, setCursor] = useState(0);
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | submitting | submitted | error
  const [submitMessage, setSubmitMessage] = useState('');

  const flat = useMemo(() => flattenQuestions(instrument), [instrument]);
  const total = flat.length;

  // ---- stage transitions ----

  const beginFromBrochure = () => setStage('gate');

  const handleGateSubmit = async (fields) => {
    // POST to /api/leads (or caller-provided endpoint/function)
    try {
      let result;
      if (onLeadCapture) {
        result = await onLeadCapture({
          instrumentId: instrument.id,
          instrumentVersion: instrument.version,
          ...fields,
        });
      } else {
        const r = await fetch(leadEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            instrumentId: instrument.id,
            instrumentVersion: instrument.version,
            ...fields,
          }),
        });
        result = await parseJsonResponse(r, 'Could not reach the assessment service.');
      }
      if (!result?.ok || !result?.sessionId) {
        return { ok: false, message: result?.message || 'Could not start the assessment.' };
      }
      setLead({ ...fields, sessionId: result.sessionId, attemptNumber: result.attemptNumber });
      setResponses({});
      setCursor(0);
      setStage('questions');
      return { ok: true };
    } catch (e) {
      return { ok: false, message: String(e) };
    }
  };

  const advance = (next) => {
    if (cursor + 1 < total) setCursor(cursor + 1);
    else completeAssessment(next);
  };

  const handleAnswer = (qid, optionIdx) => {
    const next = { ...responses, [qid]: optionIdx };
    setResponses(next);
    setTimeout(() => advance(next), 180);
  };

  // Technical-mcq items that carry a `rationale` capture both the chosen
  // option and a free-text "and why" before advancing. The rationale is stored
  // under a sibling key (`<qid>__rationale`) so it never collides with the
  // choice index the scorers read. No tap-to-advance delay — this commits on
  // an explicit Continue.
  const handleAnswerRationale = (qid, optionIdx, rationale) => {
    const next = { ...responses, [qid]: optionIdx, [rationaleKey(qid)]: rationale };
    setResponses(next);
    advance(next);
  };

  const goBack = () => {
    if (cursor > 0) setCursor(cursor - 1);
  };

  const completeAssessment = async (finalResponses) => {
    const axisScores = computeAxisScores(instrument, finalResponses);
    const resolution = resolveQuadrant(instrument, axisScores);
    const technicalScore = computeTechnicalScore(instrument, finalResponses);
    const payload = {
      responses: finalResponses,
      axisScores,
      technicalScore,
      quadrantId: resolution.quadrant?.id,
      completedAt: new Date().toISOString(),
    };
    setStage('result');
    setSubmitStatus('submitting');
    if (seedDemo) {
      setSubmitStatus('submitted');
      setSubmitMessage('Demo mode — result not saved.');
      return;
    }
    try {
      let result;
      if (onSubmit) {
        result = await onSubmit(lead.sessionId, payload);
      } else {
        const url = submissionEndpoint.replace(':sessionId', lead.sessionId);
        const r = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        result = await parseJsonResponse(r, 'Could not save your result. Please contact the shop.');
      }
      if (result?.ok) {
        setSubmitStatus('submitted');
        setSubmitMessage(result.message || '');
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result?.message || 'Could not save your result. Please contact the shop.');
      }
    } catch (e) {
      setSubmitStatus('error');
      setSubmitMessage(String(e));
    }
  };

  // ---- compute result derived values ----

  const axisScores = useMemo(
    () => (stage === 'result' ? computeAxisScores(instrument, responses) : null),
    [stage, instrument, responses]
  );
  const resolution = useMemo(
    () => (axisScores ? resolveQuadrant(instrument, axisScores) : null),
    [axisScores, instrument]
  );
  const technicalScore = useMemo(
    () => (stage === 'result' ? computeTechnicalScore(instrument, responses) : null),
    [stage, instrument, responses]
  );

  // ---- render ----

  return (
    <div className="via-root">
      {stage === 'brochure' && (
        <BrochureIntro
          instrument={instrument}
          branding={branding}
          onBegin={beginFromBrochure}
        />
      )}

      {stage === 'gate' && (
        <EmailGate
          instrument={instrument}
          branding={branding}
          onBack={() => setStage('brochure')}
          onSubmit={handleGateSubmit}
        />
      )}

      {stage === 'questions' && flat[cursor] && (
        <QuestionScreen
          key={flat[cursor].question.id}
          flat={flat}
          cursor={cursor}
          total={total}
          selected={responses[flat[cursor].question.id]}
          selectedRationale={responses[rationaleKey(flat[cursor].question.id)]}
          onAnswer={handleAnswer}
          onAnswerRationale={handleAnswerRationale}
          onBack={goBack}
        />
      )}

      {stage === 'result' && resolution && (
        <ResultScreen
          instrument={instrument}
          axisScores={axisScores}
          resolution={resolution}
          technicalScore={technicalScore}
          submitStatus={submitStatus}
          submitMessage={submitMessage}
          lead={lead}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email gate
// ---------------------------------------------------------------------------

const DEV_PREFILL = {
  name: 'Kelly Smith',
  email: 'kelly+demo@example.com',
  phone: '',
  experienceYears: '',
};

function EmailGate({ instrument, branding, onBack, onSubmit }) {
  const config = instrument.leadCapture || {};
  const [form, setForm] = useState(() => (import.meta.env.DEV ? { ...DEV_PREFILL } : {}));
  const [consent, setConsent] = useState(() => Boolean(import.meta.env.DEV));
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const fields = config.fields || ['name', 'email'];
  const fieldDefs = {
    name: { label: 'Full name', type: 'text', required: true },
    email: { label: 'Email', type: 'email', required: true },
    phone: { label: 'Phone (optional)', type: 'tel', required: false },
    experienceYears: {
      label: 'Years of professional mechanic experience',
      type: 'number',
      required: false,
    },
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (config.consentLine && !consent) return;
    setStatus('submitting');
    const result = await onSubmit(form);
    if (!result.ok) {
      setStatus('error');
      setMessage(result.message || 'Could not start the assessment.');
    }
  };

  return (
    <section className="via-card via-gate">
      <button className="via-link-btn" onClick={onBack}>← Back</button>
      {branding?.companyName && <div className="via-brand">{branding.companyName}</div>}
      <h1 className="via-title">{config.gateHeading || 'Tell us who you are'}</h1>
      {config.gateBlurb && <p className="via-intro-copy">{config.gateBlurb}</p>}

      <form className="via-lead-form" onSubmit={handleSubmit}>
        <div className="via-lead-fields">
          {fields.map((f) => {
            const def = fieldDefs[f];
            if (!def) return null;
            return (
              <label key={f} className="via-field">
                <span>{def.label}</span>
                <input
                  type={def.type}
                  required={def.required}
                  value={form[f] || ''}
                  onChange={(e) => update(f, e.target.value)}
                />
              </label>
            );
          })}
        </div>
        {config.consentLine && (
          <label className="via-consent">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              required
            />
            <span>{config.consentLine}</span>
          </label>
        )}
        <button
          type="submit"
          className="via-btn via-btn-primary"
          disabled={status === 'submitting' || (config.consentLine && !consent)}
        >
          {status === 'submitting' ? 'Starting…' : (config.cta || 'Start the assessment')}
        </button>
        {status === 'error' && <div className="via-form-error">{message}</div>}
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Question screen
// ---------------------------------------------------------------------------

function QuestionScreen({ flat, cursor, total, selected, selectedRationale, onAnswer, onAnswerRationale, onBack }) {
  const { group, question } = flat[cursor];
  const isFirstInGroup = cursor === 0 || flat[cursor - 1].group.id !== group.id;
  const progress = ((cursor + 1) / total) * 100;

  return (
    <section className="via-card via-question-card">
      <div className="via-progress-row">
        <button className="via-link-btn" onClick={onBack} aria-label="Go back" disabled={cursor === 0}>
          ← Back
        </button>
        <div className="via-progress-text">
          Question {cursor + 1} of {total}
        </div>
      </div>
      <div className="via-progress-bar">
        <div className="via-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {isFirstInGroup && (
        <div className="via-group-header">
          <div className="via-group-eyebrow">{group.title}</div>
          {group.description && <p className="via-group-desc">{group.description}</p>}
        </div>
      )}

      {question.type === 'technical-mcq' && (
        <div className="via-tech-eyebrow">Technical</div>
      )}
      {question.type === 'open-response' && (
        <div className="via-tech-eyebrow">Written answer</div>
      )}
      <h2 className="via-prompt">{question.prompt || question.stem}</h2>

      {question.type === 'open-response' ? (
        <OpenResponse
          question={question}
          initial={typeof selected === 'string' ? selected : ''}
          onSubmit={onAnswer}
        />
      ) : question.type === 'likert-4' ? (
        <div className="via-likert-row">
          {question.options.map((opt, i) => (
            <button
              key={i}
              className={`via-likert-option ${selected === i ? 'via-likert-option-selected' : ''}`}
              onClick={() => onAnswer(question.id, i)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : question.type === 'technical-mcq' && question.rationale ? (
        <TechnicalMcqRationale
          question={question}
          initialChoice={typeof selected === 'number' ? selected : null}
          initialRationale={typeof selectedRationale === 'string' ? selectedRationale : ''}
          onSubmit={onAnswerRationale}
        />
      ) : (
        <div className="via-options">
          {question.options.map((opt, i) => (
            <button
              key={i}
              className={`via-option ${selected === i ? 'via-option-selected' : ''}`}
              onClick={() => onAnswer(question.id, i)}
            >
              <span className="via-option-letter">{String.fromCharCode(65 + i)}</span>
              <span className="via-option-label">{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Open-response (free-text) item
//
// Unlike the tap-to-advance MCQ/likert items, a free-text answer needs a
// textarea and an explicit commit. Answering is optional — a blank is itself
// signal (the candidate skipped the written question) and never blocks
// completion. The typed string is stored in `responses` and is reviewer-read
// only: it feeds neither the axis scores nor the technical score.
// ---------------------------------------------------------------------------

function OpenResponse({ question, initial, onSubmit }) {
  const [text, setText] = useState(initial || '');
  const trimmed = text.trim();
  return (
    <div className="via-open">
      <textarea
        className="via-open-input"
        placeholder={question.placeholder || 'Type your answer…'}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        autoFocus
      />
      <div className="via-open-actions">
        <button
          type="button"
          className="via-btn via-btn-primary"
          onClick={() => onSubmit(question.id, trimmed)}
        >
          {trimmed ? 'Continue' : 'Skip this question'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Technical MCQ with a free-text rationale ("and why?")
//
// A technical-mcq item that declares `rationale` captures both the chosen
// option (which feeds the technical score) and a written explanation (which is
// reviewer-read, never scored). Unlike the tap-to-advance plain MCQ, this needs
// an explicit Continue so the candidate can pick AND type. When
// `rationale.required` is true, Continue stays disabled until both are present.
// ---------------------------------------------------------------------------

function TechnicalMcqRationale({ question, initialChoice, initialRationale, onSubmit }) {
  const [choice, setChoice] = useState(initialChoice);
  const [text, setText] = useState(initialRationale || '');
  const trimmed = text.trim();
  const required = question.rationale?.required === true;
  const canContinue = choice != null && (!required || trimmed.length > 0);

  return (
    <div className="via-options">
      {question.options.map((opt, i) => (
        <button
          key={i}
          className={`via-option ${choice === i ? 'via-option-selected' : ''}`}
          onClick={() => setChoice(i)}
        >
          <span className="via-option-letter">{String.fromCharCode(65 + i)}</span>
          <span className="via-option-label">{opt.label}</span>
        </button>
      ))}
      <div className="via-open via-rationale">
        <label className="via-rationale-label">
          {question.rationale?.prompt || 'And why?'}
        </label>
        <textarea
          className="via-open-input"
          placeholder={question.rationale?.placeholder || 'Explain your reasoning…'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
        />
        <div className="via-open-actions">
          <button
            type="button"
            className="via-btn via-btn-primary"
            disabled={!canContinue}
            onClick={() => onSubmit(question.id, choice, trimmed)}
          >
            Continue
          </button>
        </div>
        {!canContinue && (
          <div className="via-rationale-hint">
            {choice == null
              ? 'Choose an answer and explain your reasoning to continue.'
              : 'Add a brief explanation to continue.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result screen
// ---------------------------------------------------------------------------

function ResultScreen({ instrument, axisScores, resolution, technicalScore, submitStatus, submitMessage, lead }) {
  const { quadrant, indeterminate, xScore, yScore } = resolution;
  const fitClass = quadrant?.fit ? `via-fit-${quadrant.fit}` : '';
  const techEnabled = instrument.technicalScoring?.enabled && technicalScore && technicalScore.total > 0;
  const techLabel = instrument.technicalScoring?.label || 'Technical reasoning';

  return (
    <section className="via-card via-result">
      <div className={`via-result-header ${fitClass}`}>
        <div className="via-eyebrow">
          {lead?.attemptNumber && lead.attemptNumber > 1
            ? `Your profile (attempt ${lead.attemptNumber})`
            : 'Your profile'}
        </div>
        <h1 className="via-title">{quadrant?.label || 'Result'}</h1>
        {quadrant?.headline && <p className="via-headline">{quadrant.headline}</p>}
      </div>

      <div className="via-result-body">
        <div className="via-chart-wrap">
          <QuadrantChart
            instrument={instrument}
            xScore={xScore}
            yScore={yScore}
            resultQuadrantId={quadrant?.id}
          />
        </div>
        <div className="via-result-text">
          <h3>What this means</h3>
          <p>{quadrant?.description}</p>
          <div className="via-score-breakdown">
            {instrument.axes.map((axis) => {
              const score = axisScores[axis.id];
              const pct = Math.round(((score + 1) / 2) * 100);
              return (
                <div key={axis.id} className="via-score-row">
                  <div className="via-score-axis">{axis.label}</div>
                  <div className="via-score-bar">
                    <div className="via-score-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="via-score-ends">
                    <span>{axis.lowLabel}</span>
                    <span>{axis.highLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {techEnabled && (
        <div className={`via-tech-report via-tech-${techBand(technicalScore.percent)}`}>
          <div className="via-eyebrow">{techLabel}</div>
          <div className="via-tech-headline">
            <strong>{technicalScore.correct} of {technicalScore.total}</strong> correct
            <span className="via-tech-percent">({technicalScore.percent}%)</span>
          </div>
          <div className="via-tech-bar">
            <div className="via-tech-fill" style={{ width: `${technicalScore.percent}%` }} />
          </div>
          <p className="via-tech-note">
            Knowledge questions are scored separately from your profile above —
            different mechanics with the same profile can have different technical depth.
          </p>
        </div>
      )}

      {(() => {
        const openCount = instrument.groups
          .flatMap((g) => g.questions)
          .filter((q) => q.type === 'open-response').length;
        if (openCount === 0) return null;
        return (
          <div className="via-written-note">
            <p>
              You also answered {openCount} written{' '}
              {openCount === 1 ? 'question' : 'questions'}. Those aren't scored
              automatically — the shop reads them by hand.
            </p>
          </div>
        );
      })()}

      {quadrant?.postResultMessage && (
        <div className={`via-post-result via-fit-${quadrant.fit}`}>
          <p>{quadrant.postResultMessage}</p>
        </div>
      )}

      {submitStatus === 'submitting' && (
        <div className="via-status-line">Saving your result…</div>
      )}
      {submitStatus === 'error' && (
        <div className="via-form-error">
          {submitMessage} (Your result is shown above — the shop will reach out if needed.)
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Admin / instrument preview
// ---------------------------------------------------------------------------

function AdminView({ instrument }) {
  return (
    <section className="via-card via-admin">
      <div className="via-eyebrow">Admin preview</div>
      <h1 className="via-title">{instrument.title}</h1>
      <p className="via-subtitle">
        Instrument <code>{instrument.id}</code> v{instrument.version} •{' '}
        {totalQuestions(instrument)} questions • {instrument.groups.length} groups
      </p>

      <h3>Axes</h3>
      <ul className="via-admin-axes">
        {instrument.axes.map((a) => (
          <li key={a.id}>
            <strong>{a.label}</strong> <code>({a.id})</code>: {a.lowLabel} ↔ {a.highLabel}
          </li>
        ))}
      </ul>

      <h3>Quadrants</h3>
      <ul className="via-admin-quads">
        {instrument.quadrants.map((q) => (
          <li key={q.id}>
            <strong>{q.label}</strong> — {q.fit.toUpperCase()} — x:{q.x}, y:{q.y}
            <div className="via-admin-desc">{q.description}</div>
          </li>
        ))}
      </ul>

      <h3>Items</h3>
      {instrument.groups.map((g) => (
        <div key={g.id} className="via-admin-group">
          <h4>{g.title}</h4>
          {g.description && <p>{g.description}</p>}
          <ol>
            {g.questions.map((q) => {
              const isDisposition = q.type === 'likert-4' || q.type === 'forced-choice';
              return (
                <li key={q.id}>
                  <div className="via-admin-prompt">
                    <code className="via-admin-type">{q.type}</code>{' '}
                    {q.prompt || q.stem}
                  </div>
                  {Array.isArray(q.options) ? (
                    <ul>
                      {q.options.map((o, i) => (
                        <li key={i}>
                          <code>
                            {isDisposition
                              ? `${o.axis} ${o.score > 0 ? '+' : ''}${o.score}`
                              : o.correct
                              ? '✓ correct'
                              : '·'}
                          </code>{' '}
                          — {o.label}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="via-admin-open">[open-response — free text, reviewer-read]</div>
                  )}
                  {q.rationale && (
                    <div className="via-admin-open">
                      + written rationale{q.rationale.required ? ' (required)' : ''} — reviewer-read, unscored
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </section>
  );
}

// Read a fetch Response as JSON without throwing on empty / non-JSON bodies
// (e.g. a 404 from a Vite dev server with no backend wired). Returns a shaped
// { ok, message? } object the callers already know how to handle.
async function parseJsonResponse(response, fallbackMessage) {
  const text = await response.text().catch(() => '');
  if (!text) {
    return {
      ok: false,
      message: response.ok
        ? fallbackMessage
        : `${fallbackMessage} (HTTP ${response.status})`,
    };
  }
  try {
    return JSON.parse(text);
  } catch {
    return {
      ok: false,
      message: `${fallbackMessage} (HTTP ${response.status})`,
    };
  }
}
